const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class MarketingService {
    constructor() {
        this.token = null;
        // 🛡️ Map активных запросов: ключ → Promise.
        // Используется для защиты от двойных запросов на изменяющие операции
        // (покупки, оплаты). Если такой же запрос уже выполняется — второй
        // вызов сразу вернёт ошибку, не отправляя ничего на сервер.
        this._inFlightRequests = new Map();
    }

    /**
     * Защита от дублирующихся запросов.
     * Если запрос с таким же ключом уже выполняется — возвращает ошибку
     * без отправки на сервер. Иначе выполняет запрос и автоматически
     * убирает ключ из Map после завершения (успех или ошибка).
     *
     * @param {string} key - уникальный ключ запроса (обычно endpoint + параметры)
     * @param {Function} requestFn - async функция, выполняющая сам запрос
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    async _dedupedRequest(key, requestFn) {
        if (this._inFlightRequests.has(key)) {
            console.warn(`[MarketingService] Дубликат запроса заблокирован: ${key}`);
            return {
                success: false,
                message: 'Запрос уже выполняется. Дождитесь завершения предыдущего запроса.'
            };
        }

        const promise = requestFn();
        this._inFlightRequests.set(key, promise);

        try {
            return await promise;
        } finally {
            // Обязательно убираем ключ — даже при исключении,
            // иначе кнопка останется заблокированной навсегда.
            this._inFlightRequests.delete(key);
        }
    }

    getHeaders() {
        // Получаем токен из localStorage каждый раз
        const token = localStorage.getItem('auth_token') || localStorage.getItem('auth_token');

        if (!token || token === 'demo token') {
            console.error('No valid auth token found');
        }

        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
    }
    async getClonesInfo(data) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/clones/info`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    program: data.program || 1,
                    tarif: data.tarif,
                    level: data.level,
                    number_level: data.number_level
                }),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error getting clones info:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

// Получить все клоны пользователя
    async getAllClones() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/clones/all`, {
                method: 'GET',
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error getting all clones:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }
    async handleResponse(response) {
        const data = await response.json();
        return data;
    }

    // Получить доступные тарифы и уровни
    async getLightBoxOptions(program = 1) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/lightbox/options?program=${program}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Box options:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // Проверить активацию пользователя
    async checkActivation() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/lightbox/check-activation`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error checking activation:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // НОВЫЙ МЕТОД: Проверить статус для конкретного тарифа и уровня
    async checkStatus(tarif, level) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/lightbox/check-status`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ tarif, level }),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error checking status:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // Получить корзину
    async getBasket() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/basket`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching basket:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // Добавить в корзину
    async addToBasket(data) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/basket/add`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(data),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error adding to basket:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // Удалить из корзины
    async removeFromBasket(id) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/basket/${id}`, {
                method: 'DELETE',
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error removing from basket:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // Очистить корзину
    async clearBasket() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/basket`, {
                method: 'DELETE',
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error clearing basket:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // НОВЫЙ МЕТОД: Оплатить корзину
    async payBasket() {
        // 🛡️ Защита от дублирующихся запросов оплаты корзины
        const key = `payBasket`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/basket/pay`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error paying basket:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    // НОВЫЙ МЕТОД: Получить информацию о первом уровне
    async getFirstLevel(tarif) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/lightbox/first-level`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ tarif }),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error getting first level:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // НОВЫЙ МЕТОД: Купить конкретный уровень
    async buyLevel(tarif, level) {
        // 🛡️ Защита от дублирующихся запросов покупки
        const key = `buyLevel:${tarif}:${level}`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/lightbox/buy-level`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                    body: JSON.stringify({ tarif, level }),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error buying level:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    // Просмотр позиции в дереве
    async viewPosition(data) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/view-position`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({
                    program: data.program || 1,
                    tarif: data.tarif,
                    level: data.level,
                    number_level: data.number_level,
                    mentor_id: data.mentor_id || 0
                }),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error viewing position:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    async bronPosition(data) {
        // 🛡️ Защита от дублирующихся запросов бронирования
        const key = `bronPosition:${data.tarif}:${data.level}:${data.number_level}:${data.mentor_id}:${data.position}`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/bron-position`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                    body: JSON.stringify(data),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error bron position:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    async cancelBron(data) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/cancel-bron`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(data),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error cancel bron:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    async payPosition(data) {
        // 🛡️ Защита от дублирующихся запросов оплаты позиции.
        // Ключ включает mentor_id + position — это уникально идентифицирует
        // конкретное место в дереве, на которое юзер покупает клона.
        const key = `payPosition:${data.tarif}:${data.level}:${data.number_level}:${data.mentor_id}:${data.position}`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/pay-position`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                    body: JSON.stringify(data),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error pay position:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    async checkLevelActivation(tarif, level, program = 1) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/check-level-activation`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ program, tarif, level }),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error checking level activation:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    async buyLevelDirect(tarif, level) {
        // 🛡️ Защита от дублирующихся запросов покупки уровня
        const key = `buyLevelDirect:${tarif}:${level}`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/buy-level-direct`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                    body: JSON.stringify({ tarif, level }),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error buying level direct:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    async searchLoginInTree(query, tarif, level, program = 1) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/search-login`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify({ program, login: query, tarif, level }),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error searching login in tree:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // ── ПОДПИСКА НА АКТИВНОСТЬ ─────────────────────────────────────────

    /**
     * Получить статус маркетинговой подписки текущего пользователя.
     * Ответ содержит поле subscribed_until (ISO-строка) или null.
     */
    async getActivityStatus() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/activity-status`, {
                method: 'GET',
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error getting activity status:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Продлить маркетинговую подписку на 6 месяцев (стоимость 5 USD).
     * При успехе на счёт начисляется клон в тариф Start уровень 1.
     */
    async renewActivity() {
        // 🛡️ Защита от дублирующихся запросов продления подписки
        const key = `renewActivity`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/renew-activity`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error renewing activity:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    // ── ПОДПИСКА НА АКТИВНОСТЬ ─────────────────────────────────────────

    async getActivityStatus() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/activity-status`, {
                method: 'GET',
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error getting activity status:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    async renewActivity() {
        // 🛡️ Защита от дублирующихся запросов продления подписки
        const key = `renewActivity`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/renew-activity`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error renewing activity:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    // ── АНАЛИЗ ЦЕПОЧЕК ─────────────────────────────────────────────────

    async analyzeChain(params) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/analyze-chain`, {
                method: 'POST',
                headers: this.getHeaders(),
                body: JSON.stringify(params),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error analyzing chain:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // ========================================
    // ========== LIGHT MIX METHODS ==========
    // ========================================

    /**
     * Получить текущий активный розыгрыш Light Mix
     */
    async getLightMixCurrentDraw() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/current-draw`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix current draw:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить историю розыгрышей Light Mix
     */
    async getLightMixHistory(limit = 20) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/history?limit=${limit}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix history:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить статус пользователя в розыгрыше Light Mix
     */
    async getLightMixUserStatus(drawId) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/user-status/${drawId}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix user status:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Купить место в розыгрыше Light Mix
     */
    async purchaseLightMixPlace(data) {
        // 🛡️ Защита от дублирующихся запросов покупки места в Light Mix.
        // Сериализуем data в ключ — разные параметры = разные ключи.
        const key = `purchaseLightMixPlace:${JSON.stringify(data)}`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/light-mix/purchase`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                    body: JSON.stringify(data),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error purchasing Light Mix place:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    /**
     * Получить дерево маркетинга Light Mix
     */
    async getLightMixTree(program, mentorId = null) {
        try {
            const params = new URLSearchParams({ program });
            if (mentorId) {
                params.append('mentor_id', mentorId);
            }
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/tree?${params}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix tree:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить дерево Light Mix по draw_id, tarif, level
     */
    async getLightMixTreeData(drawId, positionId = null, tarif = 'mix', level = 4) {
        try {
            const params = new URLSearchParams({
                draw_id: drawId,
                tarif: tarif,
                level: level
            });
            if (positionId) {
                params.append('position_id', positionId);
            }
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/tree?${params}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix tree data:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить список тарифов
     */
    async getLightMixTarifs() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/tarifs`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix tarifs:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить уровни для тарифа
     */
    async getLightMixLevels(tarif = 'mix') {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/levels?tarif=${tarif}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix levels:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Поиск в дереве Light Mix
     */
    async searchLightMix(drawId, query, tarif = 'mix', level = 4) {
        try {
            const params = new URLSearchParams({
                draw_id: drawId,
                query: query,
                tarif: tarif,
                level: level
            });
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/search?${params}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error searching Light Mix:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить статистику пользователя по Light Mix
     */
    async getLightMixUserStats() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/user-stats`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix user stats:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Получить информацию о конкретном розыгрыше Light Mix
     */
    async getLightMixDrawInfo(drawId) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/draw/${drawId}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Mix draw info:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Проверить статус покупки Light Mix
     */
    async checkLightMixPurchaseStatus(program) {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-mix/purchase-status/${program}`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error checking Light Mix purchase status:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    // ========================================
    // ======== LIGHT RIVERS (ПРЕДЗАПУСК) =====
    // ========================================

    /**
     * Получить статус предварительной брони пользователя в Light Rivers.
     * Ожидаемый ответ data: {
     *   is_preordered: bool,
     *   position: int|null,   // позиция в предварительной очереди
     *   total: int|null,      // всего записей в очереди
     *   created_at: string|null, // ISO дата/время активации
     *   launch_at: string|null   // ISO дата/время старта (опционально, перекрывает фронтовую константу)
     * }
     */
    async getLightRiversPreorderStatus() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-rivers/preorder-status`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Rivers preorder status:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }

    /**
     * Купить одно стартовое место (предзапуск) и записать в предварительную очередь.
     * Одна покупка на аккаунт. Бэкенд проверяет баланс/подписку, списывает 50$
     * (45$ активация + 5$ комьюнити) и создаёт запись в очереди.
     * Ожидаемый ответ data: { position, total, created_at }
     */
    async createLightRiversPreorder() {
        // 🛡️ Защита от дублирующихся запросов покупки места
        const key = `createLightRiversPreorder`;
        return this._dedupedRequest(key, async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/marketing/light-rivers/preorder`, {
                    method: 'POST',
                    headers: this.getHeaders(),
                });
                return await this.handleResponse(response);
            } catch (error) {
                console.error('Error creating Light Rivers preorder:', error);
                return {
                    success: false,
                    message: 'Ошибка соединения с сервером'
                };
            }
        });
    }

    /**
     * «Участвовать»: после старта зарезервировавший попадает в список на расстановку.
     */
    async joinLightRivers() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-rivers/join`, {
                method: 'POST',
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error joining Light Rivers:', error);
            return { success: false, message: 'Ошибка соединения с сервером' };
        }
    }

    /**
     * Сводка по кластеру Light Rivers (read-only): открыта ли расстановка,
     * статус предзаказа, мои места по уровням и прогресс закрытия.
     */
    async getLightRiversOverview() {
        try {
            const response = await fetch(`${API_BASE_URL}/marketing/light-rivers/overview`, {
                headers: this.getHeaders(),
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.error('Error fetching Light Rivers overview:', error);
            return {
                success: false,
                message: 'Ошибка соединения с сервером'
            };
        }
    }
}

// ИСПРАВЛЕННЫЙ ЭКСПОРТ - именованный экспорт
export const marketingService = new MarketingService();

// Также оставляем default экспорт для обратной совместимости
export default marketingService;
